
import { useMemo } from 'react'
import { numberPrecision } from '../../helpers/UtilityHelper';
import { serviceTypeLabel } from './serviceTypeLabel'

export const Metrics = [
    { label: 'Overall Delivery %', value: 'overallDelivery', count: 'totalPickedupCount', type: 'delivery' },
    { label: 'First Attempt Adherence %', value: 'firstAttemptAdherence', count: 'totalDeliveryAttemptedCount', type: 'delivery' },
    { label: 'COD Delivery %', value: 'codDeliveryPercentage', count: 'codCount', type: 'delivery' },
    { label: 'Prepaid Delivery %', value: 'prepaidDeliveryPercentage', count: 'prepaidCount', type: 'delivery' },

    { label: 'TAT Adherence %', value: 'tatAdherence', count: 'totalDeliveredShipmentCount', type: 'tat' },
    { label: 'TAT+1 Adherence %', value: 'tatPlusOneAdherence', count: 'deliveredInTATPlusOneCount', type: 'tat' },
    { label: 'TAT+2 Adherence %', value: 'tatPlusTwoAdherence', count: 'deliveredInTATPlusTwoCount', type: 'tat' },
    { label: 'TAT+3 Adherence %', value: 'tatPlusThreeAdherence', count: 'deliveredInTATPlusThreeCount', type: 'tat' },

    { label: 'RVP TAT Adherence %', value: 'rvpPickupAdherence', count: 'totalRVPRequestCount', type: 'rvpPickup' },
    { label: 'RVP+1 Adherence %', value: 'rvpPickupPlusOneAdherence', count: 'pickedupInTATPlusOneCount', type: 'rvpPickup' },
    { label: 'RVP+2 Adherence %', value: 'rvpPickupPlusTwoAdherence', count: 'pickedupInTATPlusTwoCount', type: 'rvpPickup' },
    { label: 'RVP+3 Adherence %', value: 'rvpPickupPlusThreeAdherence', count: 'pickedupInTATPlusThreeCount', type: 'rvpPickup' },
]

export const metricLookup = new Map(Metrics.map(metric => [metric.value, metric]))

const getMetricValue = (
    data,
    courier,
    metricType,
    metricValue,
    metricCount
) => {
    switch (metricType) {
        case 'delivery':
            return {
                value: data.deliveryPerformance.get(courier)?.[metricValue] ?? 0,
                count: data.deliveryPerformance.get(courier)?.[metricCount] ?? 0
            }
        case 'tat':
            return {
                value: data.tatPerformance.get(courier)?.[metricValue] ?? 0,
                count: data.tatPerformance.get(courier)?.[metricCount] ?? 0
            }
        case 'rvpPickup':
            return {
                value: data.rvpPickupPerformance.get(courier)?.[metricValue] ?? 0,
                count: data.rvpPickupPerformance.get(courier)?.[metricCount] ?? 0
            }
        default:
            return {
                value: 0,
                count: 0
            }
    }
}

const getMetricValueTrend = (
    date,
    data,
    courier,
    metricType,
    metricValue,
    metricCount
) => {
    switch (metricType) {
        case 'delivery':
            return {
                value: data.deliveryPerformanceTrend.find(trend => trend.name === date)?.data.get(courier)?.[metricValue] ?? 0,
                count: data.deliveryPerformanceTrend.find(trend => trend.name === date)?.data.get(courier)?.[metricCount] ?? 0
            }
        case 'tat':
            return {
                value: data.tatPerformanceTrend.find(trend => trend.name === date)?.data.get(courier)?.[metricValue] ?? 0,
                count: data.tatPerformanceTrend.find(trend => trend.name === date)?.data.get(courier)?.[metricCount] ?? 0
            }
        case 'rvpPickup':
            return {
                value: data.rvpPickupPerformanceTrend.find(trend => trend.name === date)?.data.get(courier)?.[metricValue] ?? 0,
                count: data.rvpPickupPerformanceTrend.find(trend => trend.name === date)?.data.get(courier)?.[metricCount] ?? 0
            }
        default:
            return {
                value: 0,
                count: 0
            }
    }
}


export function useBenchmarkComparison(props) {
    const { data, selectedCouriers, selectedMetrics, selectedTrendAnalysisMetrics } = props;

    const overallSummary = useMemo(() => {
        if (selectedMetrics.length === 0 || selectedCouriers.length === 0) {
            return null
        }
        return selectedMetrics
            .map(metricValue => metricLookup.get(metricValue))
            .filter(metric => metric != null)
            .map(metric => {
                const metricType = metric.type
                const metricValueKey = metric.value
                const metricCountKey = metric.count
                const values = selectedCouriers.map(courier =>
                    getMetricValue(data, courier, metricType, metricValueKey, metricCountKey)
                )

                const sum = values.reduce((acc, curr) => acc + Number(curr.value), 0)
                const average = selectedCouriers.length > 0 ? sum / selectedCouriers.length : 0

                const count = values.reduce((acc, curr) => acc + Number(curr.count), 0)

                return { label: metric.label, value: average, count: count }
            })
    }, [data, selectedCouriers, selectedMetrics])

    const courierSummary = useMemo(() => {

        if (selectedMetrics.length === 0 || selectedCouriers.length === 0) {
            return null
        }

        return selectedCouriers.map((serviceType) => {
            const metrics = selectedMetrics
                .map(metricValue => metricLookup.get(metricValue))
                .filter(metric => metric != null)
                .reduce((acc, metric) => {
                    const metricType = metric.type
                    const metricValueKey = metric.value
                    const metricCountKey = metric.count
                    acc[metricValueKey] = getMetricValue(data, serviceType, metricType, metricValueKey, metricCountKey)
                    return acc
                }, {})

            return {
                label: serviceTypeLabel(serviceType),
                ...metrics
            }
        })
    }, [data, selectedCouriers, selectedMetrics])

    const trendAnalysisSummary = useMemo(() => {
        if (selectedTrendAnalysisMetrics.length === 0 || selectedCouriers.length === 0) {
            return null
        }

        const allDates = data.deliveryPerformanceTrend.map(trend => trend.name);
        return allDates.map(date => {
            const metrics = metricLookup.get(selectedTrendAnalysisMetrics[0])
            if (!metrics) {
                return null
            }

            const allCourierValues = selectedCouriers.reduce((acc, courier) => {
                acc[courier] = numberPrecision(getMetricValueTrend(date, data, courier, metrics.type, metrics.value, metrics.count).value, 2)
                return acc
            }, {})

            return {
                name: date,
                ...allCourierValues
            }
        })
    }, [data, selectedCouriers, selectedTrendAnalysisMetrics])

    return { overallSummary, courierSummary, trendAnalysisSummary }
}
